import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

const AT_BOTTOM_THRESHOLD_PX = 50

export interface ScrollAnchorReturn {
  atBottom: boolean
  scrollToBottom: () => void
  newSinceAtBottom: number
}

export function useScrollAnchor<T>(
  items: T[],
  containerRef: RefObject<HTMLElement | null>,
): ScrollAnchorReturn {
  const [atBottom, setAtBottom] = useState(true)
  const [newSinceAtBottom, setNewSinceAtBottom] = useState(0)
  const lastLengthRef = useRef(items.length)

  // Recompute atBottom on scroll
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onScroll = (): void => {
      const distance = el.scrollHeight - (el.scrollTop + el.clientHeight)
      setAtBottom(distance <= AT_BOTTOM_THRESHOLD_PX)
    }
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [containerRef])

  // When items grow: if at bottom, stick; else increment newSinceAtBottom
  useEffect(() => {
    if (items.length > lastLengthRef.current) {
      const delta = items.length - lastLengthRef.current
      if (atBottom) {
        const el = containerRef.current
        if (el) {
          if (typeof el.scrollTo === 'function') {
            el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
          } else {
            el.scrollTop = el.scrollHeight
          }
        }
      } else {
        setNewSinceAtBottom((n) => n + delta)
      }
    }
    lastLengthRef.current = items.length
  }, [items.length, atBottom, containerRef])

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current
    if (el) {
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
      } else {
        el.scrollTop = el.scrollHeight
      }
    }
    setNewSinceAtBottom(0)
    setAtBottom(true)
  }, [containerRef])

  return { atBottom, scrollToBottom, newSinceAtBottom }
}
